import React, { useState, useEffect, useCallback } from 'react';
import { fpBackButton, fpCard, fpHeader, fpHeaderTitle, fpSticker, fpStickerSmall } from '../utils/fpUi';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, RefreshCw, Newspaper, Calendar, ExternalLink, X } from 'lucide-react';
import { useDarkMode } from '../contexts/DarkModeContext';
import { useLanguage } from '../contexts/LanguageContext';

const UPDATES_URL = 'https://raw.githubusercontent.com/elchinoga/FootParadise/main/updates.json';
const BASE_IMAGE_URL = 'https://raw.githubusercontent.com/elchinoga/FootParadise/main/';

export default function Updates() {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const { language } = useLanguage();

  const [updates, setUpdates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);


  const fetchUpdates = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      // Try to fetch from GitHub JSON file
      const response = await fetch(UPDATES_URL);
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();

      if (data.updates && data.updates.length > 0) {
        setUpdates(data.updates);
      }
    } catch (err) {
      console.error('Error fetching updates:', err);
      setError(language === 'es' ? 'Error al cargar las noticias' : 'Error loading news');
    }

    setIsLoading(false);
  }, [language]);

  useEffect(() => {
    fetchUpdates();
  }, [fetchUpdates]);


  const handleOpenTwitter = async (url) => {
    if (!url) return;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return BASE_IMAGE_URL + image;
  };

  const formatDate = (dateStr) => {
    try {
      // Parse date without timezone conversion (treat as local date)
      const [year, month, day] = dateStr.split('-').map(Number);
      const date = new Date(year, month - 1, day); // month is 0-indexed
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const isToday = date.getTime() === today.getTime();
      
      if (isToday) {
        return language === 'es' ? 'HOY' : 'TODAY';
      }
      
      const options = { month: 'short', day: 'numeric' };
      return date.toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US', options);
    } catch {
      return dateStr;
    }
  };

  const texts = {
    en: {
      title: 'News & Updates',
      loading: 'Loading updates...',
      retry: 'Retry',
      openTwitter: 'View on X',
      noUpdates: 'No updates yet',
      tapToExpand: 'Tap to expand',
    },
    es: {
      title: 'Noticias',
      loading: 'Cargando noticias...',
      retry: 'Reintentar',
      openTwitter: 'Ver en X',
      noUpdates: 'Sin noticias aún',
      tapToExpand: 'Toca para ampliar',
    }
  };
  
  const txt = texts[language] || texts.en;

  return (
    <div style={{
      ...styles.container,
      ...(isDarkMode ? styles.containerDark : {}),
    }}>
      {/* Header */}
      <div style={{
        ...styles.header,
        ...(isDarkMode ? styles.headerDark : {}),
      }}>
        <button onClick={() => navigate('/')} style={{
          ...styles.backButton,
          ...(isDarkMode ? styles.backButtonDark : {}),
        }}>
          <ArrowLeft size={20} color={isDarkMode ? "#000" : "#fff"} />
        </button>
        <div style={styles.headerCenter}>
          <Newspaper size={20} color="#FFD700" />
          <h2 style={styles.headerTitle}>{txt.title}</h2>
        </div>
        <button onClick={fetchUpdates} style={styles.refreshButton}>
          <RefreshCw size={18} color="#fff" />
        </button>
      </div>


      {/* Content */}
      <div style={styles.content}>
        {/* Loading State */}
        {isLoading && (
          <div style={styles.loadingContainer}>
            <div style={styles.spinner}></div>
            <p style={styles.loadingText}>{txt.loading}</p>
          </div>
        )}

        {/* Error State */}
        {error && !isLoading && (
          <div style={styles.errorContainer}>
            <p style={styles.errorText}>{error}</p>
            <button onClick={fetchUpdates} style={styles.retryButton}>
              <RefreshCw size={16} />
              <span>{txt.retry}</span>
            </button>
          </div>
        )}

        {/* Updates List */}
        {!isLoading && !error && updates.length === 0 && (
          <div style={styles.emptyContainer}>
            <Newspaper size={48} color="rgba(255,255,255,0.3)" />
            <p style={styles.emptyText}>{txt.noUpdates}</p>
          </div>
        )}

        {!isLoading && !error && updates.length > 0 && (
          <div style={styles.updatesList}>
            {updates.map((item, index) => (
              <div 
                key={item.id || index} 
                style={{
                  ...styles.updateCard,
                  ...(isDarkMode ? styles.updateCardDark : {}),
                }}
              >
                {/* Image */}
                {item.image && (
                  <div 
                    style={styles.updateImageContainer}
                    onClick={() => setSelectedImage(getImageUrl(item.image))}
                  >
                    <img 
                      src={getImageUrl(item.image)} 
                      alt={item.title}
                      style={styles.updateImage}
                      loading="lazy"
                    />
                    <div style={styles.expandHint}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/>
                      </svg>
                      <span style={styles.expandHintText}>{txt.tapToExpand}</span>
                    </div>
                  </div>
                )}
                
                {/* Content */}
                <div style={styles.updateContent}>
                  <div style={styles.updateHeader}>
                    <span style={styles.updateDate}>
                      <Calendar size={12} />
                      {formatDate(item.date)}
                    </span>
                  </div>
                  <h3 style={styles.updateTitle}>{item.title}</h3>
                  <p style={styles.updateDescription}>{item.description}</p>
                  
                  {item.twitter && (
                    <button 
                      onClick={() => handleOpenTwitter(item.twitter)}
                      style={styles.twitterButton}
                    >
                      <ExternalLink size={14} />
                      <span>{txt.openTwitter}</span>
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Image Modal */}
      {selectedImage && (
        <div style={styles.imageModal} onClick={() => setSelectedImage(null)}>
          <button style={styles.imageModalClose} onClick={() => setSelectedImage(null)}>
            <X size={24} color="#fff" />
          </button>
          <img src={selectedImage} alt="Full size" style={styles.imageModalImage} />
        </div>
      )}

      {/* Animations */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>
  );
}

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a2e',
    display: 'flex',
    flexDirection: 'column',
  },
  containerDark: {
    backgroundColor: '#000000',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 20px',
    backgroundColor: '#1a1a2e',
    color: 'white',
    borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
    position: 'sticky',
    top: 0,
    zIndex: 100,
    ...fpHeader,
  },
  headerDark: {
    backgroundColor: '#000000',
    borderBottom: '1px solid #333',
  },
  backButton: {
    background: 'linear-gradient(135deg, #FF1493 0%, #FF69B4 100%)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(255, 20, 147, 0.4)',
    ...fpBackButton,
  },
  backButtonDark: {
    background: '#ffffff',
    boxShadow: '0 2px 8px rgba(255, 255, 255, 0.2)',
  },
  headerCenter: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  headerTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
    margin: 0,
    ...fpHeaderTitle,
  },
  refreshButton: {
    background: 'rgba(255, 255, 255, 0.15)',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    ...fpBackButton,
  },
  content: {
    flex: 1,
    padding: '16px',
    overflowY: 'auto',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '3px solid rgba(255, 20, 147, 0.3)',
    borderTop: '3px solid #FF1493',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  loadingText: {
    color: 'rgba(255, 255, 255, 0.7)',
    marginTop: '16px',
    fontSize: '14px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '40px 20px',
  },
  errorText: {
    color: '#ff6b6b',
    marginBottom: '16px',
  },
  retryButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '10px 20px',
    background: 'rgba(255, 255, 255, 0.15)',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    ...fpSticker,
  },
  emptyContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
  },
  emptyText: {
    color: 'rgba(255, 255, 255, 0.5)',
    marginTop: '16px',
  },
  updatesList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  updateCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
    borderRadius: '16px',
    overflow: 'hidden',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    ...fpCard,
  },
  updateCardDark: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
  },
  updateImageContainer: {
    width: '100%',
    maxHeight: '250px',
    overflow: 'hidden',
    cursor: 'pointer',
    position: 'relative',
  },
  updateImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  expandHint: {
    position: 'absolute',
    bottom: '10px',
    right: '10px',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '6px 10px',
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    borderRadius: '20px',
    backdropFilter: 'blur(4px)',
  },
  expandHintText: {
    color: '#fff',
    fontSize: '11px',
    fontWeight: '500',
  },
  updateContent: {
    padding: '16px',
  },
  updateHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: '8px',
  },
  updateDate: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#FF1493',
    fontSize: '12px',
    fontWeight: '600',
  },
  updateTitle: {
    color: '#fff',
    fontSize: '18px',
    fontWeight: 'bold',
    margin: '0 0 8px 0',
  },
  updateDescription: {
    color: 'rgba(255, 255, 255, 0.75)',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: '0 0 12px 0',
    whiteSpace: 'pre-line',
  },
  twitterButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    padding: '8px 14px',
    background: '#1DA1F2',
    color: '#fff',
    border: 'none',
    borderRadius: '20px',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    ...fpStickerSmall,
  },
  // App Update Banner
  updateBanner: {
    padding: '12px 16px',
    borderBottom: '1px solid rgba(255,255,255,0.08)',
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  updateBannerDark: {
    backgroundColor: 'rgba(255,255,255,0.03)',
    borderBottom: '1px solid #222',
  },
  bannerRow: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  bannerText: {
    color: 'rgba(255,255,255,0.8)', fontSize: 13, flex: 1,
  },
  checkBtn: {
    display: 'flex', alignItems: 'center', gap: 8, width: '100%',
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #FF1493, #FF69B4)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontSize: 14, fontWeight: '600', cursor: 'pointer',
    justifyContent: 'center',
    boxShadow: '0 2px 10px rgba(255,20,147,0.35)',
    ...fpStickerSmall,
  },
  recheckBtn: {
    background: 'rgba(255,255,255,0.12)', border: 'none', cursor: 'pointer',
    padding: '6px', borderRadius: '50%',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#fff',
  },
  versionChip: {
    backgroundColor: 'rgba(255,255,255,0.12)',
    color: 'rgba(255,255,255,0.6)', fontSize: 11,
    padding: '2px 8px', borderRadius: 20,
  },
  miniSpinner: {
    width: 16, height: 16,
    border: '2px solid rgba(255,20,147,0.3)',
    borderTop: '2px solid #FF1493',
    borderRadius: '50%',
    animation: 'spin 0.9s linear infinite',
    flexShrink: 0,
  },
  availableWrap: {
    display: 'flex', flexDirection: 'column', gap: 8,
  },
  versionRow: {
    display: 'flex', alignItems: 'center', gap: 8,
  },
  versionLabel: {
    color: 'rgba(255,255,255,0.7)', fontSize: 12,
  },
  versionArrow: {
    color: 'rgba(255,255,255,0.4)', fontSize: 14,
  },
  downloadBtn: {
    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
    padding: '10px 16px',
    background: 'linear-gradient(135deg, #00c97a, #00a060)',
    color: '#fff', border: 'none', borderRadius: 10,
    fontSize: 14, fontWeight: '700', cursor: 'pointer',
    boxShadow: '0 2px 10px rgba(0,180,100,0.35)',
    ...fpSticker,
  },

  // Image Modal
  imageModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.95)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    animation: 'fadeIn 0.2s ease',
  },
  imageModalClose: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'rgba(255, 255, 255, 0.2)',
    border: 'none',
    borderRadius: '50%',
    padding: '10px',
    cursor: 'pointer',
  },
  imageModalImage: {
    maxWidth: '95%',
    maxHeight: '85%',
    objectFit: 'contain',
    borderRadius: '8px',
  },
};
